/**
 * HTML Cleaner
 * Cleans raw clipboard HTML from Google Docs, Word, and other external sources
 * Removes tracking URLs, unwanted styles, fixes structure issues
 */

/**
 * Cleans raw clipboard HTML (especially from Google Docs)
 * and converts it to AST-compatible clean HTML
 */
export const cleanClipboardHtml = (rawHtml: string): string => {
  // Create a temporary div to work with the HTML
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = rawHtml

  // Helper function to preserve inline formatting while extracting content
  const preserveInlineFormatting = (element: Element): string => {
    // Clone the element to avoid modifying the original
    const clone = element.cloneNode(true) as Element

    // Convert Google Docs links to proper anchor tags BEFORE processing spans
    const linkElements = Array.from(clone.querySelectorAll('a'))
    linkElements.forEach((link) => {
      // Preserve the href and clean up other attributes
      const href = link.getAttribute('href')
      if (href) {
        // Remove Google Docs tracking and keep clean URL
        const cleanHref = href.replace(/^https:\/\/www\.google\.com\/url\?q=([^&]+).*/, '$1')
        link.setAttribute('href', decodeURIComponent(cleanHref))

        // Remove Google Docs styling but keep the link
        link.removeAttribute('style')
        link.removeAttribute('target')
        link.removeAttribute('data-href')
      }
    })

    // Convert Google Docs spans to semantic HTML tags BEFORE removing styles
    const spanElements = Array.from(clone.querySelectorAll('span'))
    spanElements.forEach((span) => {
      const style = span.getAttribute('style') || ''

      // Comprehensive bold detection for Google Docs
      const isBold =
        style.includes('font-weight:700') ||
        style.includes('font-weight:bold') ||
        style.includes('font-weight: 700') ||
        style.includes('font-weight: bold') ||
        /font-weight:\s*([5-9]\d\d)/.test(style) // Any weight >= 500

      // Comprehensive italic detection
      const isItalic = style.includes('font-style:italic') || style.includes('font-style: italic')

      // Underline detection
      const isUnderline =
        style.includes('text-decoration:underline') ||
        style.includes('text-decoration: underline') ||
        style.includes('text-decoration-line:underline')

      // Preserve color and font-family for semantic meaning
      const hasColor = /color:\s*(?!#000000|#000|black|rgb\(0,\s*0,\s*0\))/.test(style)
      const hasFont = /font-family:\s*(?!Arial)/.test(style) // Keep non-default fonts

      if (isBold || isItalic || isUnderline || hasColor || hasFont) {
        // Create the innermost content first
        let content = span.innerHTML

        // Build nested elements from inside out
        if (isBold) {
          content = `<strong>${content}</strong>`
        }

        if (isItalic) {
          content = `<em>${content}</em>`
        }

        if (isUnderline) {
          content = `<u>${content}</u>`
        }

        // Add color/font styling if needed
        if (hasColor || hasFont) {
          let preservedStyle = ''

          if (hasColor) {
            const colorMatch = style.match(/color:\s*([^;]+)/)
            if (colorMatch) {preservedStyle += `color:${colorMatch[1].trim()};`}
          }

          if (hasFont) {
            const fontMatch = style.match(/font-family:\s*([^;]+)/)
            if (fontMatch) {preservedStyle += `font-family:${fontMatch[1].trim()};`}
          }

          if (preservedStyle) {
            content = `<span style="${preservedStyle}">${content}</span>`
          }
        }

        // Replace the span with the new formatted content
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = content
        while (tempDiv.firstChild) {
          span.parentNode?.insertBefore(tempDiv.firstChild, span)
        }
        span.remove()
      } else {
        // Span has no semantic meaning, unwrap it
        while (span.firstChild) {
          span.parentNode?.insertBefore(span.firstChild, span)
        }
        span.remove()
      }
    })

    // Handle text direction preservation for block elements
    const blockElements = clone.querySelectorAll('p, div, h1, h2, h3, h4, h5, h6')
    blockElements.forEach((block) => {
      const dir = block.getAttribute('dir')
      const style = block.getAttribute('style') || ''

      // Check for RTL direction
      if (dir === 'rtl' || style.includes('direction:rtl') || style.includes('direction: rtl')) {
        block.setAttribute('dir', 'rtl')
      } else if (dir === 'ltr' || style.includes('direction:ltr') || style.includes('direction: ltr')) {
        block.setAttribute('dir', 'ltr')
      }
    })

    // Now remove style attributes from remaining elements but keep the tags and preserved attributes
    const inlineElements = clone.querySelectorAll('span, strong, b, em, i, u')
    inlineElements.forEach((el) => {
      // Only remove style if it doesn't contain color or font-family
      const style = el.getAttribute('style') || ''
      if (!style.includes('color:') && !style.includes('font-family:')) {
        el.removeAttribute('style')
      }
    })

    // Remove other Google Docs attributes but preserve semantic ones
    const allElements = clone.querySelectorAll('*')
    allElements.forEach((el) => {
      // Keep: href, dir, and minimal style attributes
      // Remove: Google Docs specific attributes
      const attrsToRemove = ['class', 'id', 'aria-level', 'role', 'data-href', 'target']
      attrsToRemove.forEach((attr) => el.removeAttribute(attr))
    })

    return clone.innerHTML
  }

  // FIRST: Process ALL elements for formatting preservation BEFORE any other processing
  // This ensures Google Docs spans are converted to semantic HTML before any cleaning can strip them
  const allElementsWithSpans = tempDiv.querySelectorAll('*')
  allElementsWithSpans.forEach((element) => {
    const spans = element.querySelectorAll('span')
    if (spans.length > 0) {
      // Apply format preservation directly to preserve inline formatting
      const preservedContent = preserveInlineFormatting(element)
      element.innerHTML = preservedContent
    }
  })

  // Handle Google Docs list structures specifically
  const handleGoogleDocsList = () => {
    // First, clean up all list items - remove p tags and attributes
    const listItems = tempDiv.querySelectorAll('li')
    listItems.forEach((li) => {
      // IMPORTANT: Save nested lists before we modify the li's content
      // Google Docs often puts nested lists as direct children of li elements
      const nestedLists: Element[] = []
      Array.from(li.children).forEach((child) => {
        if (child.tagName && ['UL', 'OL'].includes(child.tagName.toUpperCase())) {
          nestedLists.push(child)
        }
      })

      // Now clean up the p tags (only those that are direct children)
      const pTags = Array.from(li.children).filter(
        (child) => child.tagName && child.tagName.toLowerCase() === 'p'
      ) as Element[]

      pTags.forEach((p) => {
        // Preserve inline formatting from the p tag
        const formattedContent = preserveInlineFormatting(p)

        // Check if this p tag contains nested lists (shouldn't happen but be safe)
        const hasNestedList = Array.from(p.children).some(
          (child) => ['UL', 'OL'].includes(child.tagName.toUpperCase())
        )

        if (!hasNestedList) {
          // Safe to replace - no nested lists inside this p tag
          if (p.parentElement === li && pTags.length === 1 && nestedLists.length === 0) {
            // If p is the only child and no nested lists, replace entire li content
            li.innerHTML = formattedContent
          } else {
            // Otherwise unwrap the p tag but preserve its formatted content
            const tempSpan = document.createElement('span')
            tempSpan.innerHTML = formattedContent
            p.parentNode?.replaceChild(tempSpan, p)
          }
        }
      })

      // Re-append nested lists that we saved earlier
      nestedLists.forEach((nestedList) => {
        // Remove it from its current position (if still in DOM)
        if (nestedList.parentElement) {
          nestedList.remove()
        }
        // Append to the end of the li
        li.appendChild(nestedList)
      })

      // Clean up attributes (but KEEP dir for RTL/LTR support!)
      // li.removeAttribute('dir')  // ❌ DON'T remove dir - we need it for RTL!
      li.removeAttribute('aria-level')
      li.removeAttribute('role')
      li.removeAttribute('style')
      li.removeAttribute('class')
      li.removeAttribute('id')
    })

    // Clean up list attributes (but KEEP dir for RTL/LTR support!)
    const lists = tempDiv.querySelectorAll('ul, ol')
    lists.forEach((list) => {
      // list.removeAttribute('dir')  // ❌ DON'T remove dir - we need it for RTL!
      list.removeAttribute('style')
      list.removeAttribute('class')
      list.removeAttribute('id')
    })

    // Now handle format wrappers around lists - unwrap them
    const formatWrappers = tempDiv.querySelectorAll('b, strong, i, em, u')
    formatWrappers.forEach((wrapper) => {
      const lists = wrapper.querySelectorAll('ul, ol')
      if (lists.length > 0) {
        // If this wrapper only contains lists (and whitespace), unwrap it
        const hasOnlyLists = Array.from(wrapper.childNodes).every((node) => {
          return (
            (node.nodeType === Node.ELEMENT_NODE && ['UL', 'OL'].includes((node as Element).tagName)) ||
            (node.nodeType === Node.TEXT_NODE && !node.textContent?.trim())
          )
        })

        if (hasOnlyLists) {
          // Unwrap the wrapper - move all children to the parent
          while (wrapper.firstChild) {
            wrapper.parentElement?.insertBefore(wrapper.firstChild, wrapper)
          }
          wrapper.remove()
        }
      }
    })
  }

  /**
   * Fix invalid nested list structure from Google Docs
   * Google Docs puts nested <ul>/<ol> as siblings of <li> instead of children
   * Example invalid structure:
   *   <ul>
   *     <li>item 1</li>
   *     <ul><li>nested item</li></ul>  <!-- SIBLING, not CHILD! -->
   *     <li>item 2</li>
   *   </ul>
   * 
   * This function moves sibling lists inside the previous <li>
   */
  const fixNestedListStructure = () => {
    // Find all ul/ol elements
    const allLists = Array.from(tempDiv.querySelectorAll('ul, ol'))
    
    allLists.forEach((list) => {
      const children = Array.from(list.childNodes)
      
      for (let i = 0; i < children.length; i++) {
        const node = children[i]
        
        // Check if this is a ul/ol that's a direct child of another ul/ol
        if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as Element
          const tagName = element.tagName.toUpperCase()
          
          if (tagName === 'UL' || tagName === 'OL') {
            // This is a nested list at the wrong level (sibling of li)
            // Find the previous li element
            let prevLi: Element | null = null
            
            for (let j = i - 1; j >= 0; j--) {
              const prevNode = children[j]
              if (prevNode.nodeType === Node.ELEMENT_NODE) {
                const prevElement = prevNode as Element
                if (prevElement.tagName.toUpperCase() === 'LI') {
                  prevLi = prevElement
                  break
                }
              }
            }
            
            if (prevLi) {
              // Move this nested list inside the previous li
              prevLi.appendChild(element)
            }
          }
        }
      }
    })
  }

  // Apply Google Docs list cleanup
  handleGoogleDocsList()
  
  // Fix invalid nested list structure from Google Docs
  // Google Docs puts nested <ul>/<ol> as siblings of <li>, not children
  // We need to move them inside the previous <li>
  fixNestedListStructure()

  // Remove unwanted elements that are common in clipboard HTML
  const unwantedSelectors = [
    'meta',
    'style',
    'script',
    'link',
    'head',
    'title',
    'span[style*="color: rgb"]', // Remove spans with inline color styles
    'span[style*="font-family"]', // Remove spans with font-family styles
    'span[style*="font-size"]', // Remove spans with font-size styles
    'span[style*="background-color"]', // Remove spans with background styles
  ]

  unwantedSelectors.forEach((selector) => {
    const elements = tempDiv.querySelectorAll(selector)
    elements.forEach((el) => {
      if (el.textContent) {
        // Replace with just the text content
        const textNode = document.createTextNode(el.textContent)
        el.parentNode?.replaceChild(textNode, el)
      } else {
        el.remove()
      }
    })
  })

  // Remove all style attributes from remaining elements (except preserved formatting)
  const allElements = tempDiv.querySelectorAll('*')
  allElements.forEach((el) => {
    // Preserve basic formatting tags but remove attributes
    if (!['b', 'strong', 'i', 'em', 'u', 'ul', 'ol', 'li'].includes(el.tagName.toLowerCase())) {
      el.removeAttribute('style')
    }
    el.removeAttribute('class')
    el.removeAttribute('id')

    // Clean up remaining clipboard-specific attributes (but KEEP dir for RTL/LTR!)
    // el.removeAttribute('dir')  // ❌ DON'T remove dir - we need it for RTL/LTR!
    el.removeAttribute('aria-level')
    el.removeAttribute('role')
  })

  // Fix invalid HTML structure and wrap orphaned elements properly
  const fixHtmlStructure = () => {
    // First, unwrap any meaningless wrapper elements (like Google Docs' outer <b> with font-weight:normal)
    const unwrapMeaninglessElements = () => {
      const meaninglessSelectors = ['b[style*="font-weight:normal"]', 'b[style*="font-weight: normal"]']

      meaninglessSelectors.forEach((selector) => {
        const elements = tempDiv.querySelectorAll(selector)
        elements.forEach((element) => {
          // Move all children to parent
          while (element.firstChild) {
            element.parentElement?.insertBefore(element.firstChild, element)
          }
          element.remove()
        })
      })
    }

    // Unwrap meaningless elements (formatting is already preserved above)
    unwrapMeaninglessElements()

    // Now handle remaining structure issues
    const blockElements = ['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'blockquote', 'hr']
    const inlineElements = ['b', 'strong', 'i', 'em', 'u', 'span', 'a'] // Include 'a' for links

    // Process all direct children and group orphaned elements
    const children = Array.from(tempDiv.childNodes)
    const processedNodes: Node[] = []
    let currentOrphanGroup: Node[] = []

    const flushOrphanGroup = () => {
      if (currentOrphanGroup.length > 0) {
        const wrapper = document.createElement('p')
        currentOrphanGroup.forEach((node) => wrapper.appendChild(node))

        // Apply formatting preservation to the wrapper content
        const preservedContent = preserveInlineFormatting(wrapper)
        const newWrapper = document.createElement('p')
        newWrapper.innerHTML = preservedContent
        processedNodes.push(newWrapper)
        currentOrphanGroup = []
      }
    }

    children.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        if (child.textContent?.trim()) {
          // Non-empty text node needs wrapping
          currentOrphanGroup.push(child)
        }
        // Ignore empty/whitespace-only text nodes
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        const element = child as Element
        const tagName = element.tagName.toLowerCase()

        if (blockElements.includes(tagName)) {
          // This is a valid block element
          flushOrphanGroup()

          // For paragraph elements, preserve inline formatting
          if (tagName === 'p') {
            const preservedContent = preserveInlineFormatting(element)
            const newP = document.createElement('p')
            newP.innerHTML = preservedContent
            processedNodes.push(newP)
          } else {
            processedNodes.push(child)
          }
        } else if (inlineElements.includes(tagName) || tagName === 'br') {
          // This is an inline element or br that needs wrapping
          if (tagName === 'br') {
            // Convert standalone br to empty paragraph
            flushOrphanGroup()
            const emptyP = document.createElement('p')
            emptyP.appendChild(document.createElement('br'))
            processedNodes.push(emptyP)
          } else {
            currentOrphanGroup.push(child)
          }
        } else {
          // Unknown element, wrap it
          currentOrphanGroup.push(child)
        }
      }
    })

    // Flush any remaining orphans
    flushOrphanGroup()

    // Replace tempDiv content with processed nodes
    tempDiv.innerHTML = ''
    processedNodes.forEach((node) => tempDiv.appendChild(node))
  }

  // Apply HTML structure fixes
  fixHtmlStructure()

  // Get the cleaned HTML
  let cleanedHtml = tempDiv.innerHTML

  // Remove only empty spans and unwanted font tags, but preserve spans with content/styling
  cleanedHtml = cleanedHtml
    .replace(/<span[^>]*><\/span>/g, '') // Remove empty spans
    .replace(/<font[^>]*>/g, '')
    .replace(/<\/font>/g, '')
    .replace(/<meta[^>]*>/g, '')
    .replace(/<br\s*\/?>\s*$/, '') // Remove trailing br

  return cleanedHtml.trim()
}

